import { useEffect, useState } from "react";
import { BookOpen, Check, Copy } from "lucide-react";
import {
  api,
  isTauriEnvironment,
  KNOWLEDGE_CATEGORY_LABELS,
  type KnowledgeItem,
} from "../../lib/tauri";
import { useUiStore } from "../../stores/uiStore";

/**
 * 司令塔「今日のナレッジ」カード。usefulness 高い & 古い/未使用 を1件 pick。
 * ワンクリックでコピー + 「使った」で last_used_at 更新（pick ロジックは Rust 側）。
 * ナレッジが1件も無ければ何も描画しない（空なら null）。
 */
export function DailyKnowledgeCard() {
  const [item, setItem] = useState<KnowledgeItem | null>(null);
  const [copied, setCopied] = useState(false);
  const setAppView = useUiStore((s) => s.setAppView);
  const showToast = useUiStore((s) => s.showToast);

  const refresh = () => {
    void api
      .pickDailyKnowledge?.()
      .then((picked) => setItem(picked ?? null))
      .catch(() => setItem(null));
  };

  useEffect(() => {
    refresh();
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://knowledge-changed", () => refresh()).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      }),
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, []);

  if (!item) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast("クリップボードにコピーできませんでした");
    }
  };

  const handleUsed = () => {
    void api
      .touchKnowledgeUsed?.(item.id)
      .then(() => {
        showToast("「使った」を記録しました。次のナレッジを表示します");
        refresh();
      })
      .catch(() => showToast("記録に失敗しました"));
  };

  return (
    <section className="command-card p-4 md:p-5">
      <div className="flex items-center justify-between">
        <p className="command-muted flex items-center gap-1.5 text-sm font-semibold">
          <BookOpen className="h-4 w-4" /> 今日のナレッジ
        </p>
        <span className="rounded-full bg-hover px-2 py-0.5 text-[10px] font-medium text-muted dark:bg-white/10 dark:text-zinc-300">
          {KNOWLEDGE_CATEGORY_LABELS[item.category]}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setAppView("knowledge")}
        className="mt-2 block w-full text-left"
      >
        <p className="break-words text-base font-semibold text-text hover:text-brand-600 dark:text-white dark:hover:text-indigo-200">
          {item.title}
        </p>
      </button>
      {item.body && (
        <p className="command-dim mt-1 line-clamp-2 whitespace-pre-wrap break-words text-xs">
          {item.body}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="flex h-8 items-center gap-1.5 rounded-md bg-indigo-500 px-3 text-xs font-semibold text-white transition hover:bg-indigo-600"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" /> コピー済み
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" /> コピー
            </>
          )}
        </button>
        <button
          type="button"
          onClick={handleUsed}
          className="command-soft-control flex h-8 items-center gap-1.5 px-3 text-xs font-semibold"
        >
          <Check className="h-3.5 w-3.5" /> 使った
        </button>
      </div>
    </section>
  );
}
